import React, { useState, useEffect, useCallback } from "react";
import { useIntl } from "react-intl";
import useStyles from "./style";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  Grid,
  Typography,
  Box,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";

// Redux
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";

// Services
import VehicleApi from "Services/Vehicle";

const VehicleDelete = (props) => {
  const { openDialog, vehicleId, handleClose } = props;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const classes = useStyles();
  const dispatch = useDispatch();
  const intl = useIntl();
  const t = intl.formatMessage;

  const [vehicleData, setVehicleData] = useState(null);

  const fetchVehicleDetails = useCallback(() => {
    if (!vehicleId) return;
    dispatch(showLoader(t({ id: "vehicle.loading" })));
    VehicleApi.getVehicleById(vehicleId).then(
      (res) => {
        dispatch(hideLoader());
        if (res?.status === 200) {
          setVehicleData(res.data.data);
        }
      },
      (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message:
              err?.response?.data?.message || t({ id: "vehicle.modal.fetchError" }),
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  }, [vehicleId]);

  useEffect(() => {
    if (openDialog && vehicleId) {
      fetchVehicleDetails();
    }
  }, [openDialog, vehicleId]);

  const handleDeleteVehicle = () => {
    dispatch(showLoader(t({ id: "vehicle.modal.delete.deleting" })));

    VehicleApi.deleteVehicle(vehicleId).then(
      (res) => {
        dispatch(hideLoader());
        if (res?.status === 200) {
          dispatch(
            showSnackbar({
              message: t({ id: "vehicle.modal.delete.success" }),
              autoHideDuration: 3000,
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "success",
            })
          );
          setVehicleData(null);
          handleClose();
        }
      },
      (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message:
              err?.response?.data?.message || t({ id: "vehicle.modal.delete.error" }),
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  };

  return (
    <React.Fragment>
      <Dialog
        fullScreen={fullScreen}
        open={openDialog}
        onClose={handleClose}
        aria-labelledby="vehicle-delete-dialog-title"
        PaperProps={{ style: { borderRadius: 12, minWidth: 360 } }}
      >
        <DialogTitle id="vehicle-delete-dialog-title">
          {t({ id: "vehicle.modal.delete.title" })}
        </DialogTitle>

        <DialogContent>
          {vehicleData ? (
            <Grid
              container
              spacing={2}
              alignItems="center"
              className={classes.dialogbox}
            >
              <Grid item xs={2}>
                {vehicleData.vehicle_image ? (
                  <Box
                    component="img"
                    sx={{
                      width: 56,
                      height: 48,
                      borderRadius: "8px",
                      objectFit: "cover",
                      boxShadow: 2,
                    }}
                    src={vehicleData.vehicle_image}
                    alt="vehicle"
                  />
                ) : (
                  <DirectionsCarIcon style={{ fontSize: 40, color: "#aaa" }} />
                )}
              </Grid>

              <Grid item xs={10}>
                <div className={classes.lightText}>
                  <Typography variant="body2" fontWeight={600}>
                    {vehicleData.vehicle_brand} — {vehicleData.vehicle_name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t({ id: "vehicle.modal.delete.plate" })} {vehicleData.vehicle_no}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t({ id: "vehicle.modal.delete.color" })} {vehicleData.vehicle_color} &nbsp;|&nbsp; {t({ id: "vehicle.modal.delete.year" })}{" "}
                    {vehicleData.manufacturing_year}
                  </Typography>
                  {vehicleData.assignedDriver && (
                    <Typography variant="body2" color="primary">
                      {t({ id: "vehicle.modal.delete.assignedDriver" })} {vehicleData.assignedDriver.firstName}{" "}
                      {vehicleData.assignedDriver.lastName}
                    </Typography>
                  )}
                </div>
              </Grid>
            </Grid>
          ) : (
            <Typography variant="body2" color="textSecondary">
              {t({ id: "vehicle.modal.delete.loading" })}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            {t({ id: "vehicle.modal.cancel" })}
          </Button>
          <Button
            onClick={handleDeleteVehicle}
            color="error"
            variant="contained"
            autoFocus
          >
            {t({ id: "vehicle.modal.delete.submit" })}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default VehicleDelete;
